import {Fragment, ReactNode, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {observer} from "mobx-react-lite";

import invariant from 'tiny-invariant';
import classNames from "classnames";

import {
    type Instruction,
    type ItemMode,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import {combine} from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
    draggable,
    dropTargetForElements,
    monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import type {DragLocationHistory} from '@atlaskit/pragmatic-drag-and-drop/types';

import {ItemState, ITreeItem as TreeItemType, TreeItemProps} from "./types";

import {indentPerLevel} from './constants';
import {DependencyContext, TreeContext} from './tree-context';


function getParentLevelOfInstruction(instruction: Instruction): number {
    if (instruction.type === 'instruction-blocked') {
        return getParentLevelOfInstruction(instruction.desired);
    }
    if (instruction.type === 'reparent') {
        return instruction.desiredLevel - 1;
    }
    return instruction.currentLevel - 1;
}

function delay({waitMs: timeMs, fn}: { waitMs: number; fn: () => void }): () => void {
    let timeoutId: number | null = window.setTimeout(() => {
        timeoutId = null;
        fn();
    }, timeMs);
    return function cancel() {
        if (timeoutId) {
            window.clearTimeout(timeoutId);
            timeoutId = null;
        }
    };
}


export const TreeItemComponent = observer(<T extends TreeItemType>(
    {
        item,
        treeSource,
        mode,
        level,
        index,
        renderItemContent,
    }: TreeItemProps<T>
) => {
    const buttonRef = useRef<HTMLButtonElement>(null);

    const [state, setState] = useState<ItemState>(
        'idle',
    );
    const [instruction, setInstruction] = useState<Instruction | null>(null);
    const cancelExpandRef = useRef<(() => void) | null>(null);

    const {dispatch, uniqueContextId, getPathToItem} = useContext(TreeContext);
    const {DropIndicator, attachInstruction, extractInstruction} = useContext(DependencyContext);


    const cancelExpand = useCallback(() => {
        cancelExpandRef.current?.();
        cancelExpandRef.current = null;
    }, []);

    const clearParentOfInstructionState = useCallback(() => {
        setState((current) => (current === 'parent-of-instruction' ? 'idle' : current));
    }, []);

    // When an item has an instruction applied
    // we are highlighting it's parent item for improved clarity
    const shouldHighlightParent = useCallback(
        (location: DragLocationHistory): boolean => {
            const target = location.current.dropTargets[0];

            if (!target) {
                return false;
            }

            const instruction = extractInstruction(target.data);

            if (!instruction) {
                return false;
            }

            const targetId = target.data.id;
            invariant(typeof targetId === 'string');

            const path = getPathToItem(targetId);
            const parentLevel: number = getParentLevelOfInstruction(instruction);
            const parentId = path.slice(0, parentLevel + 1 + 1).join('/');
            return parentId === item.id;
        },
        [getPathToItem, extractInstruction, item],
    );

    useEffect(() => {
        invariant(buttonRef.current);

        function updateIsParentOfInstruction({location}: { location: DragLocationHistory }) {
            if (shouldHighlightParent(location)) {
                setState('parent-of-instruction');
                return;
            }
            clearParentOfInstructionState();
        }

        return combine(
            draggable({
                element: buttonRef.current,
                getInitialData: () => ({
                    id: item.id,
                    type: 'tree-item',
                    isOpenOnDragStart: item.isOpen,
                    uniqueContextId,
                }),
                onDragStart: ({source}) => {
                    setState('dragging');
                    // collapse open items during a drag
                    if (source.data.isOpenOnDragStart) {
                        dispatch({type: 'collapse', itemId: item.id});
                    }
                },
                onDrop: ({source}) => {
                    setState('idle');
                    if (source.data.isOpenOnDragStart) {
                        dispatch({type: 'expand', itemId: item.id});
                    }
                },
            }),
            dropTargetForElements({
                element: buttonRef.current,
                getData: ({input, element}) => {
                    const data = {id: item.id};

                    return attachInstruction(data, {
                        input,
                        element,
                        indentPerLevel,
                        currentLevel: level,
                        isCluster: !!item.isCluster,
                        mode,
                        block: !item.isCluster ? ['make-child'] : [],
                    });
                },
                canDrop: ({source}) => {
                    return source.data.type === 'tree-item' && source.data.uniqueContextId === uniqueContextId;
                },
                getIsSticky: () => true,
                onDrag: ({self, source}) => {
                    const instruction = extractInstruction(self.data);

                    if (source.data.id !== item.id) {
                        // expand after 500ms if still merging
                        if (
                            instruction?.type === 'make-child' &&
                            item.childrenIds.length &&
                            !item.isOpen &&
                            !cancelExpandRef.current
                        ) {
                            cancelExpandRef.current = delay({
                                waitMs: 500,
                                fn: () => dispatch({type: 'expand', itemId: item.id}),
                            });
                        }
                        if (instruction?.type !== 'make-child' && cancelExpandRef.current) {
                            cancelExpand();
                        }

                        setInstruction(instruction);
                        return;
                    }
                    if (instruction?.type === 'reparent') {
                        setInstruction(instruction);
                        return;
                    }
                    setInstruction(null);
                },
                onDragLeave: () => {
                    cancelExpand();
                    setInstruction(null);
                },
                onDrop: () => {
                    cancelExpand();
                    setInstruction(null);
                },
            }),
            monitorForElements({
                canMonitor: ({source}) => source.data.uniqueContextId === uniqueContextId,
                onDragStart: updateIsParentOfInstruction,
                onDrag: updateIsParentOfInstruction,
                onDrop() {
                    clearParentOfInstructionState();
                },
            }),
        );
    }, [
        dispatch,
        item,
        mode,
        level,
        cancelExpand,
        uniqueContextId,
        extractInstruction,
        attachInstruction,
        getPathToItem,
        clearParentOfInstructionState,
        shouldHighlightParent,
    ]);

    useEffect(
        function mount() {
            return function unmount() {
                cancelExpand();
            };
        },
        [cancelExpand],
    );

    const aria = (() => {
        if (!item.childrenIds.length) {
            return undefined;
        }
        return {
            'aria-expanded': item.isOpen,
            'aria-controls': `tree-item-${item.id}--subtree`,
        };
    })();

    return (
        <Fragment>
            <div
                className={classNames({
                    'TreeItem': state === 'idle'
                })}
                style={{position: 'relative'}}
            >
                <button
                    {...aria}
                    className="TreeItemContent"
                    id={`tree-item-${item.id}`}
                    ref={buttonRef}
                    type="button"
                    data-index={index}
                    data-level={level}
                    data-testid={`tree-item-${item.id}`}
                >
                    {renderItemContent({
                        item,
                        state,
                        index,
                        level
                    })}
                    {
                        instruction
                            ? <DropIndicator instruction={instruction}/>
                            : null
                    }
                </button>
            </div>
            {item.childrenIds.length && item.isOpen ? (
                <div id={aria?.['aria-controls']}>
                    {item.childrenIds.map((childTreeId, index, array) => {
                        const child = treeSource.data.items[childTreeId] as T
                        const childType: ItemMode = (() => {
                            if (child.childrenIds.length && child.isOpen) {
                                return 'expanded';
                            }

                            if (index === array.length - 1) {
                                return 'last-in-group';
                            }

                            return 'standard';
                        })();
                        return (
                            <TreeItemComponent
                                item={child}
                                treeSource={treeSource}
                                key={child.id}
                                level={level + 1}
                                mode={childType}
                                index={index}
                                renderItemContent={renderItemContent}
                            />
                        );
                    })}
                </div>
            ) : null}
        </Fragment>
    );
}) as <T extends TreeItemType>(props: TreeItemProps<T>) => ReactNode;

export default TreeItemComponent;
